import type { ReactNode } from 'react';
import { Button } from 'semantic-ui-react';

import CancelButton from '@components/cancel-button';

import { useMultiStepFormContext } from './multi-step-form-context';

export interface MultiStepFormActionsProps {
  /**
   * Label for the Back button.
   */
  backLabel?: ReactNode;
  /**
   * Label for the Cancel button.
   */
  cancelLabel?: ReactNode;
  /**
   * External processing state for the Next button, combined with the
   * internal `isValidating` state from the form context. Use this to
   * indicate side-effects (e.g. async lookups) that should block
   * step advancement.
   */
  isProcessing?: boolean;
  /**
   * Whether the form is currently submitting.
   */
  isSubmitting?: boolean;
  /**
   * Label for the Next button.
   */
  nextLabel?: ReactNode;
  /**
   * Called when the Cancel button is clicked. Step state is automatically
   * reset before this callback fires.
   *
   * When omitted, the first step shows a Back button (using `handleBack`
   * from context) instead of Cancel — useful when the form is preceded by
   * a non-form step such as a type-selection screen.
   */
  onCancel?: () => void;
  /**
   * Called when the Submit button is clicked on the last step.
   */
  onSubmit: () => void;
  /**
   * Label for the Submit button on the last step.
   */
  submitLabel?: ReactNode;
}

/**
 * Generic footer action buttons for a multi-step form.
 *
 * Renders Cancel/Back and Next/Submit buttons based on the current step,
 * reading navigation state from context.
 *
 * This component **must** be used within a {@link MultiStepFormProvider}.
 *
 * Intended for use inside a `Modal.Actions` wrapper or similar container.
 */
export const MultiStepFormActions = ({
  backLabel = 'Back',
  cancelLabel = 'Cancel',
  isProcessing,
  isSubmitting,
  nextLabel = 'Next',
  onCancel,
  onSubmit,
  submitLabel = 'Submit',
}: MultiStepFormActionsProps) => {
  const { handleBack, handleNext, isFirst, isLast, isValidating, reset } =
    useMultiStepFormContext();

  const isLoadingNext = isValidating || isProcessing;

  const handleCancelClick = () => {
    reset();
    onCancel?.();
  };

  const cancelButton = (
    <CancelButton key="cancel" onClick={handleCancelClick}>
      {cancelLabel}
    </CancelButton>
  );

  const backButton = (
    <Button key="back" onClick={handleBack} basic>
      {backLabel}
    </Button>
  );

  const nextButton = (
    <Button
      key="next"
      disabled={isLoadingNext}
      loading={isLoadingNext}
      onClick={handleNext}
      primary
    >
      {nextLabel}
    </Button>
  );

  const submitButton = (
    <Button
      key="submit"
      disabled={isSubmitting}
      loading={isSubmitting}
      onClick={onSubmit}
      primary
    >
      {submitLabel}
    </Button>
  );

  return (
    <>
      {isFirst && onCancel ? cancelButton : backButton}
      {isLast ? submitButton : nextButton}
    </>
  );
};
